<template>
  <file-info-modal
    ref="$fileInfoModal"
    :title="titleModalInfo"
    :type="typeInfoModal"
    :rule-access="ruleAccess"
    :client-id="type == 'client' ? id : 0"
    :project-id="type == 'project' ? id : 0"
  />
  <file-move-modal
    ref="$fileMoveModal"
    @success-move="onSuccessMove"
    @success-remove="removeSelectedFoldersAndFiles"
    @success-remove-file="removeFile"
    @success-remove-folder="removeFolder"
    @success-replace="onSuccessReplace"
    @success-replace-upload="onSuccessReplaceUpload"
    @success-abort-file="onSuccessAbortFile"
    @close-error="onCloseError"
    :position="modalPos"
    :type="typeCard"
    :error-type="errorType"
  />
  <div class="file-block">
    <div class="file-block__header">
      <div class="file-block__header-title">
        {{ "Файлы" }}
      </div>
      <div class="file-block__header-mode" @click="changeMode">
        {{ advancedMode ? "Стандартный режим" : "Расширенный режим" }}
      </div>
    </div>
    <div ref="$searchBlock" class="file-block__search">
      <div class="file-block__search-input">
        <app-input
          v-model="searchText"
          :show-search-button="true"
          @search="searchFolders"
          @press-enter="searchFolders"
          @press-esc="onPressEsc"
        />
      </div>
      <div class="file-block__search-hashtag">
        <app-hashtag
          ref="hashtagRef"
          class="file-item__string-hashtag"
          :items="searchTags"
          :selector-enabled="true"
          tooltip-text="Фильтр по тегу"
          :selector-value="selectedTags"
          :entity-type="TagEntityType.Folder"
          @update:selectorValue="changeTags"
          @remove="removeTag"
        />
      </div>
    </div>
    <div v-if="advancedMode" class="file-block__advanced-mode">
      <app-button
        label="Выделить всё"
        type="link"
        class="file-block__advanced-mode-button"
        @click="selectAll"
      />
      <app-button
        v-if="countSelected !== 0"
        label="Снять выделенное"
        type="link"
        class="file-block__advanced-mode-button"
        @click="deselectAll"
      />
      <div
        class="file-block__advanced-mode-icons-panel"
        v-if="countSelected !== 0"
      >
        <app-tooltip text="Скачать выбранное" style="height: 18px"
          ><template #activator>
            <app-icon
              name="download"
              width="18px"
              color="#979797"
              hover-color="#499259"
              cursor="pointer"
              @click="downloadSelectedFoldersAndFiles"
            /> </template
        ></app-tooltip>
        <template v-if="dragGeneralState.isValid">
          <app-tooltip text="Удалить выбранное" style="height: 18px"
            ><template #activator>
              <app-icon
                name="delete-outline"
                width="18px"
                color="#979797"
                hover-color="#D05A4F"
                cursor="pointer"
                @click="handleChildEvent({ type: 'removeElements' })"
              /> </template
          ></app-tooltip>
        </template>
      </div>
    </div>
    <div class="file-block__line" />
    <div ref="$folderTreeElement">
      <template v-if="!isLoading">
        <div
          v-if="folder && ((folder.Folders && folder.Files) || !searchMode)"
          class="file-block__foulders-list"
        >
          <file-folder
            v-if="folder"
            :folder="folder"
            folder-type="systemFolder"
            :root-folder="true"
            :rule-access="ruleAccess"
          />
        </div>
        <div v-else class="file-block__search-empty">Файлы не найдены</div>
      </template>
      <template v-else>
        <div class="file-block__loader">
          <app-loader class="file-block__loader-icon" />
        </div>
      </template>
    </div>
  </div>
  <file-drag-element v-if="dragGeneralState.active" />
</template>

<script setup lang="ts">
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  ref,
  watch,
} from "vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppHashtag from "@/components/ui-kit/hashtag/AppHashtag.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppLoader from "@/components/ui-kit/loader/AppLoader.vue";
import FileFolder from "@/components/file/FileFolder.vue";
import FileInfoModal from "@/components/file/modals/FileInfoModal.vue";
import FileMoveModal from "@/components/file/modals/FileMoveModal.vue";
import {
  DragEntity,
  DragGeneralState,
  DragStateEx,
  FileOut,
  FolderOut,
  TagSignable,
} from "@/models/interfaces/file/file-model";
import { FileApi } from "@/ api/FileApi";
import FileDragElement from "@/components/file/FileDragElement.vue";
import { ProjectApi } from "@/ api/ProjectApi";
import { ProjectContainer } from "@/models/interfaces/project/project-models";
import { workerStore } from "@/store/worker";
import { searchStore } from "@/store/search";
import { jobsStore } from "@/store/jobs";
import { ClientsApi } from "@/ api/ClientsApi";
import { debounce } from "lodash";
import { TagsApi } from "@/ api/TagsApi";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { TagBaseType, TagEntityType } from "@/models/etc/types";

const workerStorage = workerStore();
const searchStoreInstance = searchStore();

interface Props {
  id: number;
  type?: "project" | "client";
  ruleAccess: number;
}

const props = withDefaults(defineProps<Props>(), {
  type: "project",
  ruleAccess: 0,
});

const advancedMode = ref(false);
const searchMode = ref(false);
const searchText = ref("");
const searchTextSucces = ref("");
const changeMode = () => {
  advancedMode.value = !advancedMode.value;
};

const isAdmin= workerStorage.workerProfile?.IsAdmin;

const $fileInfoModal = ref<typeof FileInfoModal | null>(null);
const $fileMoveModal = ref<typeof FileMoveModal | null>(null);
const $folderTreeElement = ref<HTMLElement | null>(null);
const savedHeight = ref<number | null>(null);
const titleModalInfo = ref();
const typeCard = ref("error");
const errorType = ref("folder");
let container;
let containerFile;
const containerProject = ref<ProjectContainer>();
let formdataTemp: FormData;
const newDirFocus = ref(false);
const renameFolderFocus = ref(false);
const renameFileFocus = ref(false);
const dirFocusId = ref(0);
const countSelected = ref<number>(0);
const handleClearSearch = ref(0);
const openSearchFoldersIdList = ref<number[]>([]);
const searchAutoOpenEnabled = ref(true);
const ignoreWatcher = ref(false);
const openAfterMoveFolderId = ref<number | null>(null);
const pendingMoveOpenFolderId = ref<number | null>(null);
const modalPos = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const typeInfoModal = ref<"default" | "modalPermitions">("default");
const isModalOpened = ref(false);
const isLoading = ref(false);
const percentUploading = ref(0);
const preSearchHeight = ref(0);
const flashFileId = ref<number | null>(null);

let resolveReplaceUploadChoice: ((value: boolean | null) => void) | null = null;

const waitReplaceUploadChoice = (): Promise<boolean | null> => {
  return new Promise((resolve) => {
    resolveReplaceUploadChoice = resolve;
  });
};

type FolderTag = FolderOut["Tags"][number];
const searchTags = ref<TagSignable[]>([]);
const selectedTags = ref<TagWithFrequency[]>([]);
const isTagsUpdating = ref(false);
const changeTags = async (newSelectedTags: TagWithFrequency[]) => {
  if (isTagsUpdating.value) return;

  const currentFolderTags: FolderTag[] = searchTags.value ?? [];
  const nextSelectedTags: TagWithFrequency[] = newSelectedTags ?? [];

  const addedTags = nextSelectedTags.filter(
    (selectedTag) =>
      !currentFolderTags.some((folderTag) => isSameTag(folderTag, selectedTag)),
  );

  const removedTags = currentFolderTags.filter(
    (folderTag) =>
      !nextSelectedTags.some((selectedTag) =>
        isSameTag(folderTag, selectedTag),
      ),
  );

  if (addedTags.length === 0 && removedTags.length === 0) {
    return;
  }

  isTagsUpdating.value = true;

  try {
    // const success = await emitToParent?.({
    //   type: "changeFolderTags",
    //   data: {
    //     dir: props.folder,
    //     tags: nextSelectedTags.map(mapSelectorTagToFolderTag),
    //     addedTags,
    //     removedTags,
    //   },
    // });

    searchTags.value = nextSelectedTags.map(mapSelectorTagToFolderTag);
    // console.log('1',searchTags.value);

    // if (!success) {
    //   selectedTags.value = currentFolderTags.map(mapFolderTagToSelectorTag);
    // }
  } catch (error) {
    console.error("Ошибка при изменении тегов папки", error);
    selectedTags.value = currentFolderTags.map(mapFolderTagToSelectorTag);
  } finally {
    isTagsUpdating.value = false;
  }
};

const removeTag = async (item: { Id: any; Name: string }) => {
  if (isTagsUpdating.value) return;

  const nextSelectedTags = (selectedTags.value ?? []).filter(
    (tag) => !isSameTag(tag, item),
  );

  await changeTags(nextSelectedTags);
};

const isSameTag = (
  a: { Id: any; Name: string },
  b: { Id: any; Name: string },
): boolean => {
  const aIdExists = a.Id !== null && a.Id !== undefined;
  const bIdExists = b.Id !== null && b.Id !== undefined;

  if (aIdExists && bIdExists) {
    return String(a.Id) === String(b.Id);
  }

  return a.Name.trim().toLowerCase() === b.Name.trim().toLowerCase();
};

const mapFolderTagToSelectorTag = (tag: FolderTag): TagWithFrequency => ({
  ...tag,
  Frequency: 0,
  FullName: tag.Name,
  Type: TagBaseType.Common,
});

const mapSelectorTagToFolderTag = (tag: TagWithFrequency): FolderTag => ({
  Signed: false,
  SignedBy: "",
  SignedAt: null as unknown as Date,
  Id: tag.Id,
  Name: tag.Name,
});

provide("openAfterMoveFolderId", openAfterMoveFolderId);

const selectAll = () => {
  handleChildEvent({ type: "selectAll" });
};

const deselectAll = () => {
  handleChildEvent({ type: "deselect" });
};

provide("mode", advancedMode);
provide("searchMode", searchMode);
provide("searchText", searchTextSucces);
provide("containerProject", containerProject);
provide("isAdmin", isAdmin);
provide("newDirFocus", newDirFocus);
provide("dirFocusId", dirFocusId);
provide("renameFileFocus", renameFileFocus);
provide("renameFolderFocus", renameFolderFocus);
const pendingRenameFocus = ref<"folder" | "file" | "new-folder" | null>(null);
provide("openSearchFoldersIdList", openSearchFoldersIdList);
provide("modalPos", modalPos);
provide("percentUploading", percentUploading);
provide("flashFileId", flashFileId);

// FileBlock.vue
const searchOpenedFolderIds = ref<number[]>([]);
const restoreOpenedFolderIds = ref<number[]>([]);

provide("searchOpenedFolderIds", searchOpenedFolderIds);
provide("openSearchFoldersIdList", restoreOpenedFolderIds);

provide("handleClearSearch", handleClearSearch);
provide("isModalOpened", isModalOpened);
provide("searchAutoOpenEnabled", searchAutoOpenEnabled);
provide("isModalOpenedChange", (value: boolean) => {
  isModalOpened.value = value;
});
provide("disableNewDirFocus", () => {
  newDirFocus.value = false;
});
provide("disableRenameFileFocus", () => {
  renameFileFocus.value = false;
});
provide("changeElFocusId", (value: number) => {
  dirFocusId.value = value;
});
provide("disableRenameFolderFocus", () => {
  renameFolderFocus.value = false;
});
provide("changeModalPos", (value: { x: number; y: number }) => {
  modalPos.value = { ...value };
  console.log(modalPos.value);
});

const resetModalPos = () => {
  modalPos.value = null;
};

let folder = ref<FolderOut>();

let scrollObserver: IntersectionObserver | null = null;
let scrollSentinel: HTMLElement | null = null;
let scrollUnlockCleanup: (() => void) | null = null;

const getScrollParent = (el: HTMLElement): Element | Window => {
  let parent = el.parentElement;
  while (parent && parent !== document.documentElement) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    if (/(auto|scroll)/.test(overflow + overflowY)) return parent;
    parent = parent.parentElement;
  }
  return window;
};

const attachScrollUnlock = () => {
  detachScrollUnlock();
  if (!$folderTreeElement.value || savedHeight.value === null) return;

  const el = $folderTreeElement.value;
  const lockedHeight = savedHeight.value;

  el.style.minHeight = "";
  const contentHeight = el.scrollHeight;
  el.style.minHeight = `${lockedHeight}px`;

  if (contentHeight >= lockedHeight) {
    unlockHeight();
    return;
  }

  const scrollEl = getScrollParent(el);

  const getScroll = () =>
    scrollEl === window ? window.scrollY : (scrollEl as Element).scrollTop;

  const getViewportH = () =>
    scrollEl === window
      ? window.innerHeight
      : (scrollEl as Element).clientHeight;

  const elRect = el.getBoundingClientRect();
  const containerOffset =
    scrollEl === window
      ? window.scrollY
      : (scrollEl as Element).scrollTop -
        (scrollEl as Element).getBoundingClientRect().top;
  const elementTopDoc = elRect.top + containerOffset;

  const updateMinHeight = () => {
    if (!$folderTreeElement.value) {
      detachScrollUnlock();
      return;
    }

    const scroll = getScroll();
    const viewportH = getViewportH();

    const neededHeight = scroll + viewportH - elementTopDoc;

    const newMinHeight = Math.max(
      contentHeight,
      Math.min(lockedHeight, neededHeight),
    );

    savedHeight.value = newMinHeight;
    el.style.minHeight = `${newMinHeight}px`;

    if (newMinHeight <= contentHeight) {
      detachScrollUnlock();
      el.style.minHeight = "";
      el.style.overflow = "";
      savedHeight.value = null;
    }
  };

  updateMinHeight();

  (scrollEl as EventTarget).addEventListener("scroll", updateMinHeight, {
    passive: true,
  });
  scrollUnlockCleanup = () =>
    (scrollEl as EventTarget).removeEventListener("scroll", updateMinHeight);
};

const detachScrollUnlock = () => {
  scrollUnlockCleanup?.();
  scrollUnlockCleanup = null;
  if (scrollObserver) {
    scrollObserver.disconnect();
    scrollObserver = null;
  }
  scrollSentinel?.remove();
  scrollSentinel = null;
};

const onPressEsc = async () => {
  try {
    lockHeight();

    if (!searchMode.value) return;

    isLoading.value = true;
    ignoreWatcher.value = true;

    const searchTree = folder.value;
    const openedInSearch = new Set(searchOpenedFolderIds.value);

    searchMode.value = false;
    restoreOpenedFolderIds.value = [];
    searchText.value = "";
    searchTextSucces.value = "";
    changeTags([]);

    await reloadTree(searchTree, openedInSearch);

    restoreOpenedFolderIds.value = [...openedInSearch];
    searchOpenedFolderIds.value = [];

    nextTick(() => {
      ignoreWatcher.value = false;
    });
  } finally {
    isLoading.value = false;
    await nextTick();
    unlockHeight();
  }
};

const collectBranchIds = (node?: FolderOut): number[] => {
  if (!node) return [];

  return [
    node.Id,
    ...(node.Folders ?? []).flatMap((child) => collectBranchIds(child)),
  ];
};

const removeBranchFromOpened = (
  openedIds: number[],
  branch: FolderOut,
): number[] => {
  const idsToRemove = new Set(collectBranchIds(branch));
  return openedIds.filter((id) => !idsToRemove.has(id));
};

const loadFolders = async () => {
  try {
    if (props.type === "project") {
      folder.value = await FileApi.getProjectFolders(props.id);
      containerProject.value = await ProjectApi.GetProject(props.id);
    } else {
      folder.value = await FileApi.getClientFolders(props.id);
      containerProject.value = await ClientsApi.getClient(props.id);
    }
  } catch (error) {
    console.error(error);
  }
};

const searchFolders = async () => {
  if (!searchMode.value && $folderTreeElement.value) {
    preSearchHeight.value = $folderTreeElement.value.offsetHeight;
  }
  lockHeight();
  isLoading.value = true;
  try {
    folder.value =
      props.type === "project"
        ? await FileApi.projectFileSearch(
            props.id,
            searchText.value !== "" ? searchText.value : null,
            searchTags.value.map((tag) => tag.Id),
          )
        : await FileApi.clientFileSearch(
            props.id,
            searchText.value !== "" ? searchText.value : null,
            searchTags.value.map((tag) => tag.Id),
          );

    if (folder.value) {
      folder.value.Name = "Результаты поиска";
    }

    searchMode.value = true;
    searchTextSucces.value = searchText.value;

    searchOpenedFolderIds.value = collectBranchIds(folder.value);
    restoreOpenedFolderIds.value = [];
  } finally {
    isLoading.value = false;
    await nextTick();
    attachScrollUnlock();
  }
};

const consumeFileSearchRequest = async () => {
  const req = searchStoreInstance.pendingFileSearch;
  if (!req || req.type !== props.type || req.id !== props.id) return;
  searchStoreInstance.clearPendingFileSearch();

  ignoreWatcher.value = true;
  searchText.value = req.fileName;
  await nextTick();
  ignoreWatcher.value = false;

  await searchFolders();
  await nextTick();
  await nextTick();

  const findFileInTree = (currentFolder: FolderOut): FileOut | null => {
    for (const file of currentFolder.Files ?? []) {
      if (file.Id === req.fileId) return file;
    }
    for (const subfolder of currentFolder.Folders ?? []) {
      const found = findFileInTree(subfolder);
      if (found) return found;
    }
    return null;
  };

  if (folder.value) {
    const targetFile = findFileInTree(folder.value);
    if (targetFile) {
      setTimeout(() => {
        flashFileId.value = targetFile.Id;
        setTimeout(() => {
          flashFileId.value = null;
        }, 1200);
      }, 100);
    }
  }
};

watch(
  () => searchTags.value,
  async (tags) => {
    selectedTags.value = (tags ?? []).map(mapFolderTagToSelectorTag);

    if (searchText.value.length >= 3 || tags.length > 0) {
      searchFolders();
    } else if (
      tags.length == 0 &&
      searchText.value.length == 0 &&
      searchMode.value
    ) {
      lockHeight();
      try {
        isLoading.value = true;

        debouncedSearch.cancel();

        searchMode.value = false;
        searchTextSucces.value = "";
        openSearchFoldersIdList.value = [];
        handleClearSearch.value++;
        loadFolders();
      } catch (error) {
        console.log(error);
      } finally {
        isLoading.value = false;
        await nextTick();
        attachScrollUnlock();
      }
    }
  },
  { deep: true },
);

const reloadTree = async (
  searchTree?: FolderOut,
  openedInSearch: Set<number> = new Set(),
) => {
  await loadFolders();

  const projectRoot = folder.value;
  if (!searchTree || !projectRoot) return;

  if (openedInSearch.has(projectRoot.Id)) {
    restoreOpenedFolderIds.value.push(projectRoot.Id);
  }

  for (const searchBranch of searchTree.Folders ?? []) {
    await mergeSearchTreeIntoProject(projectRoot, searchBranch, openedInSearch);
  }
};

const mergeSearchTreeIntoProject = async (
  projectRoot: FolderOut,
  searchNode: FolderOut,
  openedInSearch: Set<number>,
): Promise<void> => {
  const target = findFolderById(projectRoot, searchNode);
  if (!target) return;

  const isOpened = openedInSearch.has(searchNode.Id);

  if (!isOpened) return;

  const fullFolder =
    props.type === "project"
      ? await FileApi.getChildFolders(searchNode.Id)
      : searchNode.Id !== 0 && searchNode.ProjectId !== 0
      ? await FileApi.getChildFolders(searchNode.Id)
      : searchNode;

  restoreOpenedFolderIds.value.push(searchNode.Id);

  if ((searchNode.Id !== 0 || searchNode.ProjectId !== 0) && fullFolder) {
    target.Files = fullFolder.Files;
    target.Folders = fullFolder.Folders;
    target.Editable = fullFolder.Editable;
  }

  for (const child of searchNode.Folders ?? []) {
    await mergeSearchTreeIntoProject(projectRoot, child, openedInSearch);
  }
};

const debouncedSearch = debounce(searchFolders, 1000);

watch(searchText, async (newVal, oldVal) => {
  if (ignoreWatcher.value) return;

  if (newVal !== oldVal && newVal !== "" && searchText.value.length >= 3) {
    debouncedSearch();
  } else if (!newVal && searchTags.value.length > 0) {
    debouncedSearch.cancel();
    await searchFolders();
  } else if (!newVal && searchTags.value.length === 0) {
    lockHeight();

    try {
      isLoading.value = true;

      debouncedSearch.cancel();

      searchMode.value = false;
      searchTextSucces.value = "";
      openSearchFoldersIdList.value = [];
      handleClearSearch.value++;
      await loadFolders();
    } catch (error) {
      console.log(error);
    } finally {
      isLoading.value = false;
      await nextTick();
      attachScrollUnlock();
    }
  }
});

watch(searchMode, (val) => {
  if (!val) {
    searchAutoOpenEnabled.value = true;
  }
});

const lockHeight = () => {
  detachScrollUnlock();
  if ($folderTreeElement.value) {
    const current = $folderTreeElement.value.offsetHeight;
    const h = searchMode.value
      ? savedHeight.value ?? current
      : Math.max(current, preSearchHeight.value || 0);
    if (h === 0) return;
    savedHeight.value = h;
    $folderTreeElement.value.style.minHeight = `${h}px`;
  }
};

const unlockHeight = async () => {
  detachScrollUnlock();
  await nextTick();
  if ($folderTreeElement.value) {
    $folderTreeElement.value.style.minHeight = "";
    $folderTreeElement.value.style.overflow = "";
  }
  savedHeight.value = null;
};

onBeforeUnmount(() => {
  detachScrollUnlock();
});

const findFolderById = (
  root: FolderOut,
  search: FolderOut,
): FolderOut | null => {
  if (
    (root.Id == search.Id && root.ProjectId == 0) ||
    (root.ProjectId !== 0 &&
      (search.Id == 0 || root.Name == search.Name) &&
      (root.ProjectId == search.ProjectId || root.Name == search.Name)) ||
    (root.ProjectId == 0 && search.Id == 0 && search.Name == root.Name)
  ) {
    return root;
  }

  for (const child of root.Folders ?? []) {
    const found = findFolderById(child, search);
    if (found) return found;
  }

  return null;
};

const downloadSelectedFoldersAndFiles = async () => {
  let selectedFolders: number[] = [];
  let selectedFiles: number[] = [];

  const findSelectedFoldersInTree = (currentFolder: FolderOut): FolderOut => {
    const findSelectedFilesInTree = (currentFile: FileOut): void => {
      if (currentFile.isSelected === true) {
        selectedFiles.push(currentFile.Id);
      }
    };

    if (
      currentFolder.isSelected == undefined ||
      currentFolder.isSelected == false
    ) {
      currentFolder.Files?.map((subFile) => findSelectedFilesInTree(subFile));
    } else if (currentFolder.isSelected === true) {
      selectedFolders.push(currentFolder.Id);
    }

    if (currentFolder.Folders && currentFolder.Folders.length > 0) {
      return {
        ...currentFolder,
        Folders: currentFolder.Folders?.map((subFolder) =>
          findSelectedFoldersInTree(subFolder),
        ),
      };
    }

    return currentFolder;
  };

  if (folder.value) {
    folder.value = findSelectedFoldersInTree(folder.value);
  }

  if (selectedFolders.length == 0 && selectedFiles.length == 1) {
    FileApi.FileDownload(selectedFiles[0]);
  } else if (selectedFolders.length == 1 && selectedFiles.length == 0) {
    FileApi.FolderDownload(selectedFolders[0]);
  } else if (selectedFolders.length == 0 && selectedFiles.length == 0) {
    return;
  } else {
    FileApi.FileAndFolderDownload(
      selectedFolders,
      selectedFiles,
      props.id,
      props.type,
    );
  }
};

const removeFileFromAttachedJobs = (file: FileOut) => {
  const jobIds = file.JobBindings?.map((b) => b.JobId) ?? [];
  if (jobIds.length) {
    jobsStore().removeDeletedFileFromJobs(file.Id, jobIds);
  }
};

const collectFilesInFolder = (folder: FolderOut): FileOut[] => {
  const files: FileOut[] = [...(folder.Files ?? [])];
  folder.Folders?.forEach((sub) => files.push(...collectFilesInFolder(sub)));
  return files;
};

const removeSelectedFoldersAndFiles = async () => {
  let selectedFolders: number[] = [];
  let selectedFiles: number[] = [];
  const filesToSyncWithJobs: FileOut[] = [];

  const findSelectedFoldersInTree = (currentFolder: FolderOut): FolderOut => {
    const findSelectedFilesInTree = (currentFile: FileOut): FileOut => {
      if (currentFile.isSelected === true) {
        selectedFiles.push(currentFile.Id);
        filesToSyncWithJobs.push(currentFile);
      }

      return {
        ...currentFile,
      };
    };

    if (
      currentFolder.isSelected == undefined ||
      currentFolder.isSelected == false
    ) {
      currentFolder.Files?.map((subFile) => findSelectedFilesInTree(subFile));

      currentFolder.Files = currentFolder.Files?.filter(
        (subFile) => subFile.isSelected !== true,
      );
    } else if (currentFolder.isSelected === true) {
      selectedFolders.push(currentFolder.Id);
      filesToSyncWithJobs.push(...collectFilesInFolder(currentFolder));
    }

    if (currentFolder.Folders && currentFolder.Folders.length > 0) {
      const filteredFolders = currentFolder.Folders.map((subFolder) =>
        findSelectedFoldersInTree(subFolder),
      ).filter((subFolder) => subFolder.isSelected !== true);

      return {
        ...currentFolder,
        Folders: filteredFolders,
      };
    }

    return currentFolder;
  };

  if (folder.value) {
    folder.value = findSelectedFoldersInTree(folder.value);
  }

  selectedFolders.map((subFolder) => FileApi.RemoveDir(subFolder));
  selectedFiles.map((subFile) => FileApi.RemoveFile(subFile));

  filesToSyncWithJobs.forEach((file) => removeFileFromAttachedJobs(file));
};

const removeFolder = async (dir: FolderOut) => {
  const nestedFiles = collectFilesInFolder(dir);

  await FileApi.RemoveDir(dir.Id);

  nestedFiles.forEach((file) => removeFileFromAttachedJobs(file));

  const selectFolderInTree = (currentFolder: FolderOut): FolderOut => {
    if (currentFolder.Folders && currentFolder.Folders.length > 0) {
      const filteredFolders = currentFolder.Folders.filter(
        (subFolder) => subFolder.Id !== dir.Id,
      ).map((subFolder) => selectFolderInTree(subFolder));

      return {
        ...currentFolder,
        Folders: filteredFolders,
      };
    }

    return currentFolder;
  };

  if (folder.value) {
    folder.value = selectFolderInTree(folder.value);
  }
};

const removeFile = async (dir: FolderOut, file: FileOut) => {
  await FileApi.RemoveFile(file.Id);

  removeFileFromAttachedJobs(file);

  const selectFolderInTree = (currentFolder: FolderOut): FolderOut => {
    if (currentFolder.Id === dir.Id) {
      return {
        ...currentFolder,
        Files: currentFolder.Files.filter((subFile) => subFile.Id !== file.Id),
      };
    }

    if (currentFolder.Folders && currentFolder.Folders.length > 0) {
      return {
        ...currentFolder,
        Folders: currentFolder.Folders.map((subFolder) =>
          selectFolderInTree(subFolder),
        ),
      };
    }

    return currentFolder;
  };

  if (folder.value) {
    folder.value = selectFolderInTree(folder.value);
  }
};

const setBranchSelection = (
  currentFolder: FolderOut,
  value: boolean,
): FolderOut => {
  return {
    ...currentFolder,
    isSelected: value,
    Files: (currentFolder.Files ?? []).map((file) => ({
      ...file,
      isSelected: value,
    })),
    Folders: (currentFolder.Folders ?? []).map((subFolder) =>
      setBranchSelection(subFolder, value),
    ),
  };
};

const normalizeSelectionUp = (currentFolder: FolderOut): FolderOut => {
  const normalizedFolders = (currentFolder.Folders ?? []).map((subFolder) =>
    normalizeSelectionUp(subFolder),
  );

  const normalizedFiles = (currentFolder.Files ?? []).map((file) => ({
    ...file,
  }));

  const hasChildren =
    normalizedFolders.length > 0 || normalizedFiles.length > 0;

  const allFilesSelected =
    normalizedFiles.length === 0 ||
    normalizedFiles.every((file) => file.isSelected === true);

  const allFoldersSelected =
    normalizedFolders.length === 0 ||
    normalizedFolders.every((folder) => folder.isSelected === true);

  const branchFullySelected = hasChildren
    ? allFilesSelected && allFoldersSelected
    : true;

  return {
    ...currentFolder,
    Files: normalizedFiles,
    Folders: normalizedFolders,
    isSelected:
      currentFolder.Id !== 0 &&
      currentFolder.isSelected === true &&
      branchFullySelected,
  };
};

const deselectFolders = async () => {
  const deselectFolderInTree = (currentFolder: FolderOut): FolderOut => {
    const deselectFileInTree = (currentFile: FileOut): FileOut => {
      currentFile.isSelected = false;

      return {
        ...currentFile,
      };
    };

    currentFolder.isSelected = false;
    currentFolder.Files?.map((subFile) => deselectFileInTree(subFile));

    if (currentFolder.Folders && currentFolder.Folders.length > 0) {
      return {
        ...currentFolder,
        Folders: currentFolder.Folders.map((subFolder) =>
          deselectFolderInTree(subFolder),
        ),
      };
    }

    return currentFolder;
  };

  if (folder.value) {
    folder.value = deselectFolderInTree(folder.value);
  }
};

const moveSelectedFoldersAndFiles = async (targetFolder: FolderOut) => {
  let selectedFolders: FolderOut[] = [];
  let selectedFiles: FileOut[] = [];

  const collectSelected = (currentFolder: FolderOut) => {
    currentFolder.Files?.forEach((file) => {
      if (file.isSelected === true) {
        selectedFiles.push(file);
      }
    });

    currentFolder.Folders?.forEach((folder) => {
      if (folder.isSelected === true) {
        selectedFolders.push(folder);
      } else {
        collectSelected(folder);
      }
    });
  };

  const removeSelectedFromTree = (currentFolder: FolderOut): FolderOut => {
    return {
      ...currentFolder,
      Files: (currentFolder.Files ?? []).filter(
        (file) => file.isSelected !== true,
      ),
      Folders: (currentFolder.Folders ?? [])
        .filter((folder) => folder.isSelected !== true)
        .map((folder) => removeSelectedFromTree(folder)),
    };
  };

  const insertIntoTarget = (currentFolder: FolderOut): FolderOut => {
    if (currentFolder.Id === targetFolder.Id) {
      return {
        ...currentFolder,
        Folders: [...(currentFolder.Folders ?? []), ...selectedFolders],
        Files: [...(currentFolder.Files ?? []), ...selectedFiles],
      };
    }

    return {
      ...currentFolder,
      Folders: (currentFolder.Folders ?? []).map((folder) =>
        insertIntoTarget(folder),
      ),
    };
  };

  if (!folder.value) return;

  collectSelected(folder.value);

  folder.value = removeSelectedFromTree(folder.value);

  folder.value = insertIntoTarget(folder.value);

  FileApi.MoveDir(
    targetFolder.Id,
    selectedFolders.map((element) => element.Id),
    selectedFiles.map((element) => element.Id),
  );

  deselectFolders();
};

const selectCountGeneralState = () => {
  let countSelected = 0;
  let topName = "";
  let topType = "";
  let topFound = false;

  const getFileExtension = (fileName: string): string => {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts.pop()!.toLowerCase() : "file";
  };

  const traverse = (currentFolder: FolderOut, isParentSelected = false) => {
    // Папка
    if (currentFolder.isSelected === true) {
      countSelected++;

      if (!topFound && !isParentSelected) {
        topName = currentFolder.Name;
        topType = "folder";
        topFound = true;
      }

      isParentSelected = true;
    }

    // Файлы
    currentFolder.Files?.forEach((file) => {
      if (file.isSelected === true) {
        countSelected++;

        if (!topFound && !isParentSelected) {
          topName = file.Name;
          topType = getFileExtension(file.Name);
          topFound = true;
        }
      }
    });

    // Дочерние папки
    currentFolder.Folders?.forEach((folder) =>
      traverse(folder, isParentSelected),
    );
  };

  if (!folder.value) {
    return {
      count: 0,
      name: "",
      type: "",
    };
  }

  traverse(folder.value);

  return {
    count: countSelected,
    name: topName,
    type: topType,
  };
};

const hasSelectedProjectFolder = (): boolean => {
  if (!folder.value) return false;

  const getFolderType = (
    parentType:
      | "systemFolder"
      | "systemProjectList"
      | "projectFolder"
      | "default",
    item: FolderOut,
  ): "systemFolder" | "systemProjectList" | "projectFolder" | "default" => {
    if (parentType === "systemFolder" && item.Name === "Проекты") {
      return "systemProjectList";
    }

    if (parentType === "systemProjectList") {
      return "projectFolder";
    }

    return "default";
  };

  const walk = (
    currentFolder: FolderOut,
    currentType:
      | "systemFolder"
      | "systemProjectList"
      | "projectFolder"
      | "default",
  ): boolean => {
    if (currentType === "projectFolder" && currentFolder.isSelected === true) {
      return true;
    }

    for (const child of currentFolder.Folders ?? []) {
      const childType = getFolderType(currentType, child);

      if (walk(child, childType)) {
        return true;
      }
    }

    return false;
  };

  return walk(folder.value, "systemFolder");
};

const syncSelectionState = () => {
  countSelected.value = selectCount();

  Object.assign(dragGeneralState, {
    ...selectCountGeneralState(),
    isValid: !hasSelectedProjectFolder(),
  });
};

const addFileInFolder = async (dir: number, newFile: FileOut) => {
  const updateFolderInTree = (currentFolder: FolderOut): FolderOut => {
    if (currentFolder.Id == dir) {
      if (currentFolder.isSelected === true) {
        return {
          ...currentFolder,
          Files: [
            ...(currentFolder.Files ?? []),
            { ...newFile, isSelected: true },
          ],
        };
      } else if (
        currentFolder.isSelected == undefined ||
        currentFolder.isSelected == false
      ) {
        return {
          ...currentFolder,
          Files: [...(currentFolder.Files ?? []), newFile],
        };
      }
    }

    if (currentFolder.Folders && currentFolder.Folders.length > 0) {
      return {
        ...currentFolder,
        Folders: currentFolder.Folders.map((subFolder) =>
          updateFolderInTree(subFolder),
        ),
      };
    }

    return currentFolder;
  };

  if (folder.value) {
    folder.value = updateFolderInTree(folder.value);
  }
  // console.log(folder.value);
};

const selectCount = (): number => {
  let coutntSelected = 0;
  const selectFolderInTree = (currentFolder: FolderOut): FolderOut => {
    const selectFileInTree = (currentFile: FileOut): FileOut => {
      if (currentFile.isSelected === true) {
        coutntSelected++;
      }

      return {
        ...currentFile,
      };
    };

    if (currentFolder.isSelected === true) {
      coutntSelected++;
    }
    currentFolder.Files?.map((subFile) => selectFileInTree(subFile));

    if (currentFolder.Folders && currentFolder.Folders.length > 0) {
      return {
        ...currentFolder,
        Folders: currentFolder.Folders.map((subFolder) =>
          selectFolderInTree(subFolder),
        ),
      };
    }

    return currentFolder;
  };

  if (folder.value) {
    folder.value = selectFolderInTree(folder.value);
    return coutntSelected;
  }

  return 0;
};

const handleChildEvent = async (eventData: { type: string; data?: any }) => {
  try {
    const openFolder = async (dir: FolderOut) => {
      console.log("123");
      const updatedFolder = await FileApi.getChildFolders(dir.Id);
      // console.log("open", dir);

      const updateFolderInTree = (currentFolder: FolderOut): FolderOut => {
        if (currentFolder.Id === dir.Id) {
          if (currentFolder.isSelected === true) {
            return {
              ...currentFolder,
              ...updatedFolder,
              Folders:
                updatedFolder.Folders.map((subFolder) => {
                  subFolder.isSelected = true;
                  return subFolder;
                }) || [],
              Files:
                updatedFolder.Files.map((subFile) => {
                  subFile.isSelected = true;
                  return subFile;
                }) || [],
            };
          } else if (
            currentFolder.isSelected == undefined ||
            currentFolder.isSelected == false
          ) {
            return {
              ...currentFolder,
              ...updatedFolder,
              Folders: updatedFolder.Folders || [],
              Files: updatedFolder.Files || [],
            };
          }
        }

        if (currentFolder.Folders && currentFolder.Folders.length > 0) {
          return {
            ...currentFolder,
            Folders: currentFolder.Folders.map((subFolder) =>
              updateFolderInTree(subFolder),
            ),
          };
        }

        return currentFolder;
      };

      if (folder.value) {
        folder.value = updateFolderInTree(folder.value);
      }
      // console.log(folder.value);

      // Object.assign(dragGeneralState, selectCountGeneralState());
    };

    const openProjectFolder = async (dir: FolderOut) => {
      const updatedFolder = await FileApi.getProjectFolders(dir.ProjectId);
      // console.log()

      const updateFolderInTree = (currentFolder: FolderOut): FolderOut => {
        if (currentFolder.ProjectId === dir.ProjectId) {
          if (currentFolder.isSelected === true) {
            return {
              ...currentFolder,
              // ...updatedFolder,
              Folders:
                updatedFolder.Folders.map((subFolder) => {
                  subFolder.isSelected = true;
                  return subFolder;
                }) || [],
              Files:
                updatedFolder.Files.map((subFile) => {
                  subFile.isSelected = true;
                  return subFile;
                }) || [],
            };
          } else if (
            currentFolder.isSelected == undefined ||
            currentFolder.isSelected == false
          ) {
            return {
              ...currentFolder,
              // ...updatedFolder,
              Folders: updatedFolder.Folders || [],
              Files: updatedFolder.Files || [],
            };
          }
        }

        if (currentFolder.Folders && currentFolder.Folders.length > 0) {
          return {
            ...currentFolder,
            Folders: currentFolder.Folders.map((subFolder) =>
              updateFolderInTree(subFolder),
            ),
          };
        }

        return currentFolder;
      };

      if (folder.value) {
        folder.value = updateFolderInTree(folder.value);
      }
      // console.log(folder.value);

      // Object.assign(dragGeneralState, selectCountGeneralState());
    };

    const selectFolder = async (dir: FolderOut, value: boolean) => {
      const updateTree = (currentFolder: FolderOut): FolderOut => {
        if (currentFolder.Id === dir.Id) {
          return setBranchSelection(currentFolder, value);
        }

        return {
          ...currentFolder,
          Folders: (currentFolder.Folders ?? []).map((subFolder) =>
            updateTree(subFolder),
          ),
        };
      };

      if (folder.value) {
        folder.value = normalizeSelectionUp(updateTree(folder.value));
      }

      Object.assign(dragGeneralState, selectCountGeneralState());
    };

    const selectFile = async (dirId: number, file: FileOut, value: boolean) => {
      const updateTree = (currentFolder: FolderOut): FolderOut => {
        if (currentFolder.Id === dirId) {
          return {
            ...currentFolder,
            Files: (currentFolder.Files ?? []).map((subFile) =>
              subFile.Id === file.Id
                ? { ...subFile, isSelected: value }
                : subFile,
            ),
          };
        }

        return {
          ...currentFolder,
          Folders: (currentFolder.Folders ?? []).map((subFolder) =>
            updateTree(subFolder),
          ),
        };
      };

      if (folder.value) {
        folder.value = normalizeSelectionUp(updateTree(folder.value));
      }

      Object.assign(dragGeneralState, selectCountGeneralState());
    };

    const selectAll = async () => {
      // if (props.type == "client") {
      //   dragGeneralState.isValid = false;
      // }
      const selectFolderInTree = (currentFolder: FolderOut): FolderOut => {
        const selectFileInTree = (currentFile: FileOut): FileOut => {
          currentFile.isSelected = true;

          return {
            ...currentFile,
          };
        };

        if (currentFolder.Id !== 0) {
          currentFolder.isSelected = true;
        }
        currentFolder.Files?.map((subFile) => selectFileInTree(subFile));

        if (currentFolder.Folders && currentFolder.Folders.length > 0) {
          return {
            ...currentFolder,
            Folders: currentFolder.Folders.map((subFolder) =>
              selectFolderInTree(subFolder),
            ),
          };
        }

        return currentFolder;
      };

      if (folder.value) {
        folder.value = selectFolderInTree(folder.value);
      }
    };

    const renameFolder = async (dir: number, value: string) => {
      const findFolderAndParent = (
        currentFolder: FolderOut,
        parent: FolderOut | null = null,
      ): { folder: FolderOut | null; parent: FolderOut | null } => {
        if (currentFolder.Id === dir) {
          return { folder: currentFolder, parent };
        }

        if (currentFolder.Folders?.length) {
          for (const subFolder of currentFolder.Folders) {
            const result = findFolderAndParent(subFolder, currentFolder);
            if (result.folder) return result;
          }
        }

        return { folder: null, parent: null };
      };

      if (!folder.value) return;

      const { folder: targetFolder, parent } = findFolderAndParent(
        folder.value,
      );

      if (!targetFolder) return;

      if (parent) {
        const exists = parent.Folders?.some(
          (f) => f.Name.toLowerCase() === value.toLowerCase() && f.Id !== dir,
        );

        if (exists) {
          typeCard.value = "error";
          errorType.value = "folderNameMatch";
          dirFocusId.value = dir;
          pendingRenameFocus.value = "folder";

          nextTick(() => {
            $fileMoveModal.value?.onOpen();
          });

          return;
        }
      }

      const renameInTree = (currentFolder: FolderOut): FolderOut => {
        if (currentFolder.Id === dir) {
          FileApi.RenameDir(value, currentFolder.Id);

          return {
            ...currentFolder,
            Name: value,
          };
        }

        return {
          ...currentFolder,
          Folders: currentFolder.Folders?.map(renameInTree) || [],
        };
      };

      folder.value = renameInTree(folder.value);
    };

    const renameFile = async (dir: number, file: number, value: string) => {
      const updateFolderTree = (currentFolder: FolderOut): FolderOut => {
        if (currentFolder.Id === dir) {
          const exists = currentFolder.Files?.some(
            (f) =>
              f.Name.toLowerCase() === value.toLowerCase() && f.Id !== file,
          );

          if (exists) {
            typeCard.value = "error";
            errorType.value = "fileNameMatch";
            dirFocusId.value = file;
            pendingRenameFocus.value = "file";

            nextTick(() => {
              $fileMoveModal.value?.onOpen();
            });

            return currentFolder;
          }

          return {
            ...currentFolder,
            Files: currentFolder.Files?.map((subFile) => {
              if (subFile.Id === file) {
                FileApi.RenameFile(value, subFile.Id);

                return {
                  ...subFile,
                  Name: value,
                };
              }

              return subFile;
            }),
          };
        }

        if (currentFolder.Folders?.length) {
          return {
            ...currentFolder,
            Folders: currentFolder.Folders.map(updateFolderTree),
          };
        }

        return currentFolder;
      };

      if (folder.value) {
        folder.value = updateFolderTree(folder.value);
      }
    };

    const createFolder = async (dir: FolderOut, value: string) => {
      const selectFolderInTree = async (
        currentFolder: FolderOut,
      ): Promise<FolderOut> => {
        if (currentFolder.Id === dir.Id) {
          let newDir = await FileApi.CreateNewDirectory(
            currentFolder.Id,
            value,
          );

          if (newDir == null) {
            typeCard.value = "error";
            errorType.value = "folderNameMatch";
            pendingRenameFocus.value = "new-folder";

            nextTick(() => {
              $fileMoveModal.value?.onOpen();
            });

            return {
              ...currentFolder,
            };
          }
          // newDir.ProjectId = currentFolder.ProjectId;
          // currentFolder.Folders
          //   ? currentFolder.Folders.push(newDir)
          //   : openFolder(currentFolder);
          currentFolder.Folders.push(newDir);
          if (currentFolder.isSelected == true) {
            newDir.isSelected = true;
          }
          return {
            ...currentFolder,
          };
        }

        if (currentFolder.Folders && currentFolder.Folders.length > 0) {
          const updatedFolders = await Promise.all(
            currentFolder.Folders.map((subFolder) =>
              selectFolderInTree(subFolder),
            ),
          );
          return {
            ...currentFolder,
            Folders: updatedFolders,
          };
        }

        return currentFolder;
      };

      if (folder.value) {
        folder.value = await selectFolderInTree(folder.value);
      }
    };

    const uploadFile = async (
      dir: number,
      value: string,
      formData: FormData,
    ): Promise<boolean> => {
      let isReplace = false;

      const selectFolderInTree = (currentFolder: FolderOut): FolderOut => {
        const selectFileInTree = (currentFile: FileOut): FileOut => {
          if (currentFile.Name.toLowerCase() === value.toLowerCase()) {
            isReplace = true;
          }

          return currentFile;
        };

        if (currentFolder.Id == dir) {
          return {
            ...currentFolder,
            Files: currentFolder?.Files?.map((subFile) =>
              selectFileInTree(subFile),
            ),
          };
        }

        if (currentFolder.Folders && currentFolder.Folders.length > 0) {
          return {
            ...currentFolder,
            Folders: currentFolder.Folders.map((subFolder) =>
              selectFolderInTree(subFolder),
            ),
          };
        }

        return currentFolder;
      };

      if (!folder.value) return false;

      formdataTemp = formData;
      folder.value = selectFolderInTree(folder.value);

      if (isReplace) {
        typeCard.value = "replaceUpload";
        $fileMoveModal.value?.onOpen();

        const replaceChoice = await waitReplaceUploadChoice();

        if (replaceChoice === null) {
          return false;
        }

        if (replaceChoice === true) {
          // Заменить файл
          formdataTemp.append("Type", "1");
        } else {
          // Оставить обе версии
          formdataTemp.append("Type", "0");
        }
      } else {
        formdataTemp.append("Type", "0");
      }

      try {
        const newFile = await FileApi.FileUpload(formdataTemp, (value) => {
          percentUploading.value = value;
        });

        if (!newFile) {
          // typeCard.value = "error";
          // errorType.value = "replaceUpload";
          // $fileMoveModal.value?.onOpen();
          percentUploading.value = 0;
          return false;
        }

        if (!isReplace || formdataTemp.get("Type") === "0") {
          addFileInFolder(Number(formdataTemp.get("FolderId")), newFile);
        }

        percentUploading.value = 100;
        $fileMoveModal.value?.onClose();
        return true;
      } catch (error) {
        // typeCard.value = "error";
        // errorType.value = "replaceUpload";
        // $fileMoveModal.value?.onOpen();
        // console.error('uploadFile catch:', error)
        // if (isReplace) {
        //   // При замене файл скорее всего загружен, ошибка в обработке ответа
        //   percentUploading.value = 0;
        //   $fileMoveModal.value?.onClose();
        //   return false;
        // }
        percentUploading.value = 0;
        return false;
      }
    };

    const changeFolderTags = async (
      dirId: number,
      nextTags: FolderOut["Tags"],
      addedTags: TagWithFrequency[],
      removedTags: FolderOut["Tags"],
    ) => {
      try {
        for (const tag of addedTags) {
          // await TagsApi.AddTag({EditingTagId: tag.Id, Name: tag.Name ,EntityType: TagEntityType.Folder,EntityId: dirId});
          await TagsApi.addTag({
            EditingTagId: 0,
            Name: tag.Name,
            EntityType: TagEntityType.Folder,
            EntityId: dirId,
          });
          // await TagsApi.addTag({EditingTagId: tag.Id, Name: tag.Name ,EntityType: TagEntityType.Folder,EntityId: 0});
        }

        for (const tag of removedTags) {
          await TagsApi.deleteTag({
            TagId: tag.Id,
            EntityType: TagEntityType.Folder,
            EntityId: dirId,
          });
        }

        const updateFolderTagsInTree = (
          currentFolder: FolderOut,
        ): FolderOut => {
          if (currentFolder.Id === dirId) {
            return {
              ...currentFolder,
              Tags: nextTags,
            };
          }

          return {
            ...currentFolder,
            Folders: (currentFolder.Folders ?? []).map(updateFolderTagsInTree),
          };
        };

        if (folder.value) {
          folder.value = updateFolderTagsInTree(folder.value);
        }
      } catch (error) {
        console.error("Ошибка при обновлении тегов папки", error);
        return false;
      }

      return true;
    };

    const changeFileTags = async (
      fileId: number,
      nextTags: FileOut["Tags"],
      addedTags: TagWithFrequency[],
      removedTags: FileOut["Tags"],
    ) => {
      try {
        for (const tag of addedTags) {
          await TagsApi.AddTag({
            EditingTagId: 0,
            Name: tag.Name,
            EntityType: TagEntityType.File,
            EntityId: fileId,
          });
        }

        for (const tag of removedTags) {
          await TagsApi.deleteTag({
            TagId: tag.Id,
            EntityType: TagEntityType.File,
            EntityId: fileId,
          });
        }

        const updateFileTagsInTree = (currentFolder: FolderOut): FolderOut => {
          return {
            ...currentFolder,
            Files: (currentFolder.Files ?? []).map((currentFile) =>
              currentFile.Id === fileId
                ? {
                    ...currentFile,
                    Tags: nextTags,
                  }
                : currentFile,
            ),
            Folders: (currentFolder.Folders ?? []).map(updateFileTagsInTree),
          };
        };

        if (folder.value) {
          folder.value = updateFileTagsInTree(folder.value);
        }
      } catch (error) {
        console.error("Ошибка при обновлении тегов файла", error);
        return false;
      }

      return true;
    };

    const setFileTags = (fileId: number, tags: FileOut["Tags"]) => {
      const updateFileTagsInTree = (currentFolder: FolderOut): FolderOut => {
        return {
          ...currentFolder,
          Files: (currentFolder.Files ?? []).map((currentFile) =>
            currentFile.Id === fileId
              ? {
                  ...currentFile,
                  Tags: tags,
                }
              : currentFile,
          ),
          Folders: (currentFolder.Folders ?? []).map(updateFileTagsInTree),
        };
      };

      if (folder.value) {
        folder.value = updateFileTagsInTree(folder.value);
      }
    };

    const setFolderTags = (dirId: number, tags: FolderOut["Tags"]) => {
      const updateFolderTagsInTree = (currentFolder: FolderOut): FolderOut => {
        if (currentFolder.Id === dirId) {
          return {
            ...currentFolder,
            Tags: tags,
          };
        }

        return {
          ...currentFolder,
          Folders: (currentFolder.Folders ?? []).map(updateFolderTagsInTree),
        };
      };

      if (folder.value) {
        folder.value = updateFolderTagsInTree(folder.value);
      }
    };

    switch (eventData.type) {
      case "select":
        selectFolder(eventData.data.dir, eventData.data.value);
        syncSelectionState();
        break;

      case "selectFile":
        selectFile(
          eventData.data.dir.Id,
          eventData.data.file,
          eventData.data.value,
        );
        syncSelectionState();
        break;

      case "selectAll":
        selectAll();
        syncSelectionState();
        break;

      case "deselect":
        deselectFolders();
        syncSelectionState();
        break;

      case "open":
        await openFolder(eventData.data.dir);
        syncSelectionState();
        break;

      case "openProject":
        await openProjectFolder(eventData.data.dir);
        syncSelectionState();
        break;

      case "rename":
        renameFolder(
          eventData.data.info == true
            ? eventData.data.dir
            : eventData.data.dir.Id,
          eventData.data.value,
        );
        break;

      case "renameFile":
        renameFile(
          eventData.data.info == true
            ? eventData.data.dir
            : eventData.data.dir.Id,
          eventData.data.info == true
            ? eventData.data.file
            : eventData.data.file.Id,
          eventData.data.value,
        );
        break;

      case "create":
        createFolder(eventData.data.dir, eventData.data.value);
        syncSelectionState();
        break;

      case "remove":
        typeCard.value = "removeFolder";
        $fileMoveModal.value?.onOpen(eventData.data.dir, countSelected.value);
        syncSelectionState();
        break;

      case "removeFile":
        typeCard.value = "removeFile";
        $fileMoveModal.value?.onOpen(
          eventData.data.dir,
          countSelected.value,
          eventData.data.file,
        );
        syncSelectionState();
        break;

      case "removeElements":
        resetModalPos();
        typeCard.value = "remove";
        $fileMoveModal.value?.onOpen(undefined, countSelected.value);
        syncSelectionState();
        break;

      case "download":
        FileApi.FolderDownload(eventData.data.dir.Id);
        break;

      case "upload":
        return await uploadFile(
          Number(eventData.data.formdata.get("FolderId")),
          String(eventData.data.formdata.get("FileName")),
          eventData.data.formdata,
        );

      case "abort":
        typeCard.value = "abortFile";
        $fileMoveModal.value?.onOpen(
          undefined,
          countSelected.value,
          eventData.data.file,
        );
        // FileApi.AbortUpload();
        // percentUploading.value = 0;
        break;

      case "replace":
        typeCard.value = "replace";
        formdataTemp = eventData.data.formdata;
        $fileMoveModal.value?.onOpen(
          eventData.data.targetDir,
          countSelected.value,
        );
        syncSelectionState();
        break;

      case "downloadFile":
        FileApi.FileDownload(eventData.data.file.Id);
        break;

      case "move":
        resetModalPos();
        typeCard.value = "move";
        pendingMoveOpenFolderId.value = eventData.data.openAfterMove
          ? eventData.data.targetDir.Id
          : null;

        $fileMoveModal.value?.onOpen(
          eventData.data.targetDir,
          countSelected.value,
        );
        syncSelectionState();
        break;

      case "info":
        titleModalInfo.value = "Информация о папке и её права доступа";
        container = await FileApi.GetFolderInfo(eventData.data.dir.Id);
        if (container) {
          $fileInfoModal.value?.onOpen(
            container,
            "folder",
            null,
            eventData.data.dir.Tags,
          );
        } else {
          typeCard.value = "error";
          errorType.value = "notFound";
          nextTick(() => {
            $fileMoveModal.value?.onOpen();
          });
        }
        break;

      case "infoParent":
        titleModalInfo.value = "Информация о папке и её права доступа";
        container = await FileApi.GetFolderInfo(eventData.data.dir.Id);
        container.Name = containerProject.value?.Name;
        if (container) {
          $fileInfoModal.value?.onOpen(
            container,
            "parentFolder",
            null,
            eventData.data.dir.Tags,
          );
        } else {
          typeCard.value = "error";
          errorType.value = "notFound";
          nextTick(() => {
            $fileMoveModal.value?.onOpen();
          });
        }
        break;

      case "infoFile":
        titleModalInfo.value = "Информация о файле и его права доступа";
        containerFile = await FileApi.GetFileInfo(eventData.data.file.Id);
        if (containerFile) {
          isModalOpened.value = true;
          $fileInfoModal.value?.onOpen(
            containerFile,
            "file",
            eventData.data.dir.Id,
            eventData.data.file.Tags,
          );
        } else {
          typeCard.value = "error";
          errorType.value = "notFound";
          nextTick(() => {
            $fileMoveModal.value?.onOpen();
          });
        }
        break;

      case "toggleFolder": {
        const { folderId, isOpen, folder, mode } = eventData.data;

        if (mode === "search") {
          if (isOpen) {
            if (!searchOpenedFolderIds.value.includes(folderId)) {
              searchOpenedFolderIds.value.push(folderId);
            }
          } else {
            searchOpenedFolderIds.value = removeBranchFromOpened(
              searchOpenedFolderIds.value,
              folder,
            );
          }
          break;
        }

        if (isOpen) {
          if (!restoreOpenedFolderIds.value.includes(folderId)) {
            restoreOpenedFolderIds.value.push(folderId);
          }
        } else {
          const index = restoreOpenedFolderIds.value.indexOf(folderId);
          if (index !== -1) restoreOpenedFolderIds.value.splice(index, 1);
        }
        break;
      }

      case "error":
        resetModalPos();
        typeCard.value = "error";
        errorType.value = eventData.data.errorType;

        if (eventData.data.focusTarget === "new-folder") {
          pendingRenameFocus.value = "new-folder";
        }
        // console.log(errorType.value);
        nextTick(() => {
          $fileMoveModal.value?.onOpen();
        });
        // removeFolder(eventData.data.dir);
        break;

      case "changeFolderTags":
        await changeFolderTags(
          eventData.data.dir.Id,
          eventData.data.tags,
          eventData.data.addedTags,
          eventData.data.removedTags,
        );
        break;

      case "changeFileTags":
        await changeFileTags(
          eventData.data.file.Id,
          eventData.data.tags,
          eventData.data.addedTags,
          eventData.data.removedTags,
        );
        break;

      case "setFolderTags":
        setFolderTags(eventData.data.dir.Id, eventData.data.tags);
        break;

      case "setFileTags":
        setFileTags(eventData.data.file.Id, eventData.data.tags);
        break;
    }
  } catch (error) {
    console.log(error);
    return false;
  }

  return true;
};

provide("childEventEmitter", handleChildEvent);

const onCloseError = () => {
  nextTick(() => {
    if (pendingRenameFocus.value === "folder") {
      renameFolderFocus.value = true;
    } else if (pendingRenameFocus.value === "file") {
      renameFileFocus.value = true;
    } else if (pendingRenameFocus.value === "new-folder") {
      newDirFocus.value = true;
    }

    pendingRenameFocus.value = null;
  });
};

// const onSuccessMove = (targetDir: FolderOut) => {
//   moveSelectedFoldersAndFiles(targetDir);
// };

const onSuccessMove = async (targetDir: FolderOut) => {
  moveSelectedFoldersAndFiles(targetDir);

  if (pendingMoveOpenFolderId.value === targetDir.Id) {
    openAfterMoveFolderId.value = targetDir.Id;

    await nextTick();
    openAfterMoveFolderId.value = null;
    pendingMoveOpenFolderId.value = null;
  }
};

const onSuccessReplace = (value: boolean) => {
  if (value == true) {
    formdataTemp.append("Type", "0");
  } else {
    formdataTemp.append("Type", "1");
  }
  FileApi.replaceFile(formdataTemp);

  if (value == false) {
    const folderId = formdataTemp.get("FolderId");
    const fileId = formdataTemp.get("FileId");
    const filename = formdataTemp.get("Filename");

    renameFileLocal(Number(folderId), Number(fileId), filename);
  }
};

const onSuccessReplaceUpload = (value: boolean) => {
  resolveReplaceUploadChoice?.(value);
  resolveReplaceUploadChoice = null;
};

const onSuccessAbortFile = () => {
  FileApi.AbortUpload();
  percentUploading.value = 0;
};

const renameFileLocal = async (dir: number, file: number, value: string) => {
  const selectFolderInTree = (currentFolder: FolderOut): FolderOut => {
    const selectFileInTree = (currentFile: FileOut): FileOut => {
      if (currentFile.Id === file) {
        return {
          ...currentFile,
          Name: value,
        };
      }

      return currentFile;
    };

    if (currentFolder.Id === dir) {
      return {
        ...currentFolder,
        Files: currentFolder.Files.map((subFile) => selectFileInTree(subFile)),
      };
    }

    if (currentFolder.Folders && currentFolder.Folders.length > 0) {
      return {
        ...currentFolder,
        Folders: currentFolder.Folders.map((subFolder) =>
          selectFolderInTree(subFolder),
        ),
      };
    }

    return currentFolder;
  };

  if (folder.value) {
    folder.value = selectFolderInTree(folder.value);
  }
};

const getSelectedDragEntity = (): DragEntity => {
  if (!folder.value) {
    return {
      type: "selection",
      folders: [],
      files: [],
    };
  }

  const selectedFolders: FolderOut[] = [];
  const selectedFiles: { file: FileOut; parentFolder: FolderOut }[] = [];

  const walk = (currentFolder: FolderOut, parentSelected = false) => {
    const currentSelected = currentFolder.isSelected === true;

    if (currentSelected && !parentSelected && currentFolder.Id !== 0) {
      selectedFolders.push(currentFolder);
      return;
    }

    for (const file of currentFolder.Files ?? []) {
      if (file.isSelected === true) {
        selectedFiles.push({
          file,
          parentFolder: currentFolder,
        });
      }
    }

    for (const child of currentFolder.Folders ?? []) {
      walk(child, parentSelected || currentSelected);
    }
  };

  walk(folder.value);

  return {
    type: "selection",
    folders: selectedFolders,
    files: selectedFiles,
  };
};

const dragState = reactive<DragStateEx>({
  draggedEntity: null,
  draggedElement: null,
  dropTarget: null,
  isValidDrop: false,
});

const dragGeneralState = reactive<DragGeneralState>({
  name: "",
  type: "",
  count: 0,
  active: false,
  isValid: true,
  x: 0,
  y: 0,
});

provide("dragGeneralState", dragGeneralState);

const getSelectedTopLevelFolders = (): FolderOut[] => {
  if (!folder.value) return [];

  const result: FolderOut[] = [];

  const walk = (currentFolder: FolderOut, parentSelected = false) => {
    const currentSelected = currentFolder.isSelected === true;

    if (currentSelected && !parentSelected && currentFolder.Id !== 0) {
      result.push(currentFolder);
    }

    for (const child of currentFolder.Folders ?? []) {
      walk(child, parentSelected || currentSelected);
    }
  };

  walk(folder.value);

  return result;
};

provide("getSelectedTopLevelFolders", getSelectedTopLevelFolders);

const dragTooltip = reactive({
  show: false,
  message: "",
  style: { left: "0px", top: "0px" },
});
const setDragState = (value: Partial<DragStateEx>) => {
  Object.assign(dragState, value);
};

provide("dragState", dragState);
provide("setDragState", setDragState);
provide("getSelectedDragEntity", getSelectedDragEntity);

watch(
  () => props.id,
  () => {
    loadFolders();
    searchMode.value = false;
    searchText.value = "";
  },
  { immediate: true },
);

watch(
  () => [props.id, props.type, searchStoreInstance.pendingFileSearch] as const,
  () => consumeFileSearchRequest(),
);

onMounted(() => {
  deselectFolders();
  consumeFileSearchRequest();
});
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.file-block {
  height: 100%;
  position: relative;
  width: 100%;
  gap: 8px;
  flex-direction: column;
  display: flex;

  &__header {
    display: flex;
    position: relative;
    width: 100%;
    justify-content: space-between;

    &-title {
      display: flex;
      font-family: "Roboto";
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 18px;
      color: $color-text_main;
    }

    &-mode {
      text-align: right;
      position: relative;
      cursor: pointer;
      width: max-content;
      font-family: "Roboto";
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;
      color: $color-text-secondary;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__search {
    display: inline;
    position: relative;

    &-input {
      position: relative;
      width: calc(100% - 20px);
      display: inline-block;
      margin-right: 2px;
      font-family: "Roboto";
      font-size: 14px;
      outline: none;
    }

    &-hashtag {
      display: inline;
    }

    &-empty {
      display: flex;
      justify-content: center;
      font-family: "Roboto";
      font-style: normal;
      font-size: 14px;
      line-height: 18px;
      color: $color-text_main;
      font-weight: 400;
    }
  }

  &__line {
    border-bottom: 1px solid $color-divider;
  }

  &__loader {
    position: relative;
    display: flex;
    flex-direction: column;
    // height: 100%;
    min-height: inherit;
    justify-content: center;

    &__icon {
      position: relative;
      display: flex;
      flex-direction: column;
      // height: 100%;
    }
  }

  &__advanced-mode {
    display: flex;
    gap: 12px;

    &-icons-panel {
      display: flex;
      gap: 4px;
    }
  }

  // file-block__advanced-mode
}
</style>
